import React, { useState, useEffect } from 'react';
import { filter, switchMap, tap, take } from 'rxjs/operators';
import { fromEvent } from 'rxjs';
import classNames from 'classnames';
import { leftClickEventFilter } from '../util/leftClickEventFilter';

const { default: styled } = require('styled-components');

const Styles = styled.g`
   .ghost {
      opacity: 0.33;
   }

   &:not(.controller-active) .clickable:hover:not(.disabled),
   .clickable.active {
      cursor: pointer;
      fill: var(--brand-dark-orange);
   }

   @media (prefers-color-scheme: dark) {
      &:not(.controller-active) .clickable:hover:not(.disabled),
      .clickable.active {
         fill: var(--brand-orange);
      }
   }

   .led-on-button {
      transform-box: fill-box;
      transform-origin: center center;
      text {
         fill: var(--text-color);
         font-family: var(--content-font);
         font-weight: bold;
      }
      .button-body {
         fill: var(--background-color);
         stroke: var(--text-color);
         stroke-width: 2;
      }

      .button-highlight {
         stroke: none;
         fill: var(--brand-light-orange);
      }

      .button-on-indicator {
         fill: var(--text-color);
         stroke: none;
         opacity: 0;
      }
   }
   .led-on-button.on {
      .button-on-indicator {
         fill: var(--brand-light-orange);
         opacity: 1;
      }

      .button-label {
         fill: var(--brand-light-orange);
      }
   }

   .led-on-button:not(.disabled) {
      cursor: pointer;
   }

   &:not(.controller-active) .led-on-button:hover:not(.disabled),
   .led-on-button.active {
      text {
         fill: var(--brand-dark-orange);
      }
      .button-body {
         stroke: var(--brand-dark-orange);
      }
   }

   @media (prefers-color-scheme: dark) {
      &:not(.controller-active) .led-on-button:hover:not(.disabled),
      .led-on-button.active {
         text {
            fill: var(--brand-orange);
         }
         .button-body {
            stroke: var(--brand-orange);
         }
      }
   }

   .click-through {
      text {
         fill: var(--text-color);
         font-family: var(--content-font);
         font-weight: bold;
      }
      .click-through-container {
         fill: var(--background-color);
         stroke: var(--text-color);
         stroke-width: 2;
         transform-box: fill-box;
         transform-origin: center center;
      }

      .click-through-highlight {
         stroke: none;
         fill: var(--brand-light-orange);
         transform-box: fill-box;
         transform-origin: center center;
      }
   }

   .click-through:not(.disabled) {
      cursor: pointer;
   }

   &:not(.controller-active) .click-through:hover:not(.disabled),
   .click-through.active {
      text {
         fill: var(--brand-dark-orange);
      }
      .click-through-container {
         stroke: var(--brand-dark-orange);
      }
   }

   @media (prefers-color-scheme: dark) {
      &:not(.controller-active) .click-through:hover:not(.disabled),
      .click-through.active {
         text {
            fill: var(--brand-orange);
         }
         rect {
            stroke: var(--brand-orange);
         }
      }
   }

   .knob {
      transform-box: fill-box;
      transform-origin: center center;
   }

   .knob:not(.disabled) {
      cursor: pointer;
   }

   .knob-body {
      fill: var(--background-color);
      stroke: var(--text-color);
      stroke-width: 2px;
   }
   .knob-position-indicator {
      fill: var(--text-color);
   }

   .knob-highlight {
      stroke: none;
      fill: var(--brand-light-orange);
   }

   .knob-suggestion-indicator {
      stroke-width: 4px;
      paint-order: stroke;
      fill: var(--brand-light-orange);
      stroke: var(--text-color);
   }

   &:not(.controller-active) .knob:hover:not(.disabled),
   .knob.active {
      .knob-body {
         stroke: var(--brand-dark-orange);
      }
      .knob-position-indicator {
         fill: var(--brand-dark-orange);
      }
   }

   @media (prefers-color-scheme: dark) {
      &:not(.controller-active) .knob:hover:not(.disabled),
      .knob.active {
         .knob-body {
            stroke: var(--brand-orange);
         }
         .knob-position-indicator {
            fill: var(--brand-orange);
         }
      }
   }

   &.greyscale {
      .knob-body {
         stroke: #535353 !important;
         stroke-width: 2px;
      }
      .knob-position-indicator {
         fill: #535353 !important;
      }
   }

   @media (prefers-color-scheme: dark) {
      &.greyscale {
         .knob-body {
            stroke: #b3b3b3 !important;
            stroke-width: 2px;
         }
         .knob-position-indicator {
            fill: #b3b3b3 !important;
         }
      }
   }

   .slider {
      transform-box: fill-box;
      transform-origin: center center;
   }

   .slider:not(.disabled) {
      cursor: pointer;
   }

   .slider-track-body {
      fill: transparent;
      stroke: var(--text-color);
      stroke-width: 2px;
   }

   .slider-track-highlight,
   .slider-track-handle-highlight {
      stroke: none;
      fill: var(--brand-light-orange);
   }

   .slider-track-background {
      fill: var(--background-color);
   }

   .slider-track-fill {
      fill: var(--text-color);
      fill: var(--brand-light-green);
   }

   .slider-handle-body {
      fill: var(--background-color);
      stroke: var(--text-color);
      stroke-width: 2px;
   }

   .not-applicable {
      .slider-handle-body,
      .slider-track-fill,
      .slider-track-handle-highlight,
      .knob-position-indicator {
         opacity: 0;
      }
   }

   &:not(.controller-active) .slider:hover:not(.disabled),
   .slider.active {
      .slider-track-body,
      .slider-handle-body {
         stroke: var(--brand-dark-orange);
      }

      .slider-track-fill {
         fill: var(--brand-light-orange);
      }
   }

   @media (prefers-color-scheme: dark) {
      .slider-track-fill {
         fill: var(--brand-dark-green);
      }

      &:not(.controller-active) .slider:hover:not(.disabled),
      .slider.active {
         .slider-track-body,
         .slider-handle-body {
            stroke: var(--brand-orange);
         }

         .slider-track-fill {
            fill: var(--brand-darker-orange);
         }
      }
   }

   &.greyscale {
      .slider-track-fill {
         fill: #b3b3b3 !important;
      }

      .slider-track-body {
         fill: transparent;
         stroke: #535353 !important;
         stroke-width: 2px;
      }
      .slider-handle-body {
         fill: #e3e3e3 !important;
         stroke: #535353 !important;
         stroke-width: 2px !important;
      }
   }

   @media (prefers-color-scheme: dark) {
      &.greyscale {
         .slider-track-fill {
            fill: #535353 !important;
         }

         .slider-track-body {
            fill: transparent;
            stroke: #b3b3b3 !important;
            stroke-width: 2px;
         }
         .slider-handle-body {
            fill: #2d2d2d !important;
            stroke: #b3b3b3 !important;
            stroke-width: 2px !important;
         }
      }
   }

   .incorrect-icon,
   .correct-icon {
      display: none;
      stroke-width: 3;
   }

   &.compare-answer .knob-rotate {
      transition: transform 0.2s ease;
   }

   &.compare-answer .incorrect {
      color: var(--semantic-error-text);

      .incorrect-icon {
         display: block;
      }

      .knob-body {
         fill: var(--semantic-error);
         stroke: var(--semantic-error-text);
      }
      .knob-position-indicator {
         fill: var(--semantic-error-text);
      }

      .slider-track-body {
         stroke: var(--semantic-error-text);
      }

      .slider-track-fill {
         fill: var(--semantic-error-background);
      }

      .slider-handle-body {
         fill: var(--semantic-error);
         stroke: var(--semantic-error-text);
      }

      &.click-through text {
         fill: var(--semantic-error-text);
      }

      &.click-through rect {
         stroke: var(--semantic-error-text);
         fill: var(--semantic-error);
      }
   }

   &.compare-answer:not(.controller-active) .incorrect:hover:not(.disabled),
   &.compare-answer .incorrect.active {
      .knob-body {
         fill: var(--semantic-error-interact);
         stroke: var(--semantic-error-text);
      }

      .knob-position-indicator {
         fill: var(--semantic-error-text);
      }

      .slider-track-body,
      .slider-handle-body {
         stroke: var(--semantic-error-text);
      }

      .slider-handle-body {
         fill: var(--semantic-error-interact);
      }

      .slider-track-fill {
         fill: var(--semantic-error);
      }

      &.click-through text {
         fill: var(--semantic-error-text);
      }

      &.click-through rect {
         stroke: var(--semantic-error-text);
         fill: var(--semantic-error-interact);
      }
   }

   @media (prefers-color-scheme: dark) {
      &.compare-answer .incorrect {
         .slider-track-body {
            stroke: var(--semantic-error);
         }

         .slider-track-fill {
            fill: var(--semantic-error-text);
         }
      }

      &.compare-answer:not(.controller-active) .incorrect:hover:not(.disabled),
      &.compare-answer .incorrect.active {
         .slider-track-body {
            stroke: var(--semantic-error);
         }

         .slider-track-fill {
            fill: var(--semantic-error-interact);
            fill: var(--semantic-error-background);
         }
      }
   }

   &.compare-answer .correct {
      color: var(--semantic-success-text);

      .correct-icon {
         display: block;
      }

      .knob-body {
         fill: var(--semantic-success);
         stroke: var(--semantic-success-text);
      }
      .knob-position-indicator {
         fill: var(--semantic-success-text);
      }

      .slider-track-body {
         stroke: var(--semantic-success-text);
      }

      .slider-track-fill {
         fill: var(--semantic-success-background);
      }

      .slider-handle-body {
         fill: var(--semantic-success);
         stroke: var(--semantic-success-text);
      }

      &.click-through text {
         fill: var(--semantic-success-text);
      }

      &.click-through rect {
         stroke: var(--semantic-success-text);
         fill: var(--semantic-success);
      }
   }

   &.compare-answer:not(.controller-active) .correct:hover:not(.disabled),
   &.compare-answer .correct.active {
      .knob-body {
         fill: var(--semantic-success-interact);
         stroke: var(--semantic-success-text);
      }

      .knob-position-indicator {
         fill: var(--semantic-success-text);
      }

      .slider-track-body,
      .slider-handle-body {
         stroke: var(--semantic-success-text);
      }

      .slider-handle-body {
         fill: var(--semantic-success-interact);
      }

      .slider-track-fill {
         fill: var(--semantic-success);
      }

      &.click-through text {
         fill: var(--semantic-success-text);
      }

      &.click-through rect {
         fill: var(--semantic-success-interact);
         stroke: var(--semantic-success-text);
      }
   }

   @media (prefers-color-scheme: dark) {
      &.compare-answer .correct {
         .slider-track-body {
            stroke: var(--semantic-success);
         }

         .slider-track-fill {
            fill: var(--semantic-success-text);
         }
      }

      &.compare-answer:not(.controller-active) .correct:hover:not(.disabled),
      &.compare-answer .correct.active {
         .slider-track-body {
            stroke: var(--semantic-success);
         }

         .slider-track-fill {
            fill: var(--semantic-success-interact);
            fill: var(--semantic-success-background);
         }
      }
   }
`;

const ControllerStyles = ({ children, compareAnswer, greyscale }) => {
   const [controllerActive, setControllerActive] = useState(false);

   const stylesRef = React.useRef(null);

   useEffect(() => {
      const mousedown$ = fromEvent(stylesRef.current, 'mousedown')
         .pipe(
            filter(leftClickEventFilter),
            tap(() => setControllerActive(true)),
            switchMap(() =>
               fromEvent(document, 'mouseup').pipe(
                  filter(leftClickEventFilter),
                  take(1),
                  tap(() => setControllerActive(false))
               )
            )
         )
         .subscribe();

      return () => mousedown$.unsubscribe();
   }, []);

   return (
      <Styles
         ref={stylesRef}
         className={classNames({
            'controller-active': controllerActive,
            'compare-answer': compareAnswer,
            greyscale: greyscale,
         })}
      >
         {children}
      </Styles>
   );
};

export { ControllerStyles };
